import {
  collection,
  query,
  where,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  getDoc,
  writeBatch,
} from "firebase/firestore";

import { _getFirestore } from "./firebase";
import {
  uploadWhiteboardData,
  downloadWhiteboardData,
  deleteWhiteboardData,
} from "./storage";

import type { Profile, Whiteboard, WhiteboardFolder } from "../types/profile";

// Get firestore instance
const getFirestore = () => _getFirestore();

// ============================================================================
// Profiles
// ============================================================================

export const loadProfiles = async (): Promise<Profile[]> => {
  const firestore = getFirestore();
  const profilesRef = collection(firestore, "profiles");
  const snapshot = await getDocs(profilesRef);

  return snapshot.docs.map((doc) => ({
    id: doc.id,
    ...doc.data(),
  })) as Profile[];
};

export const getProfile = async (
  profileId: string,
): Promise<Profile | null> => {
  const firestore = getFirestore();
  const profileRef = doc(firestore, "profiles", profileId);
  const snapshot = await getDoc(profileRef);

  if (!snapshot.exists()) {
    return null;
  }

  return {
    id: snapshot.id,
    ...snapshot.data(),
  } as Profile;
};

// ============================================================================
// Whiteboards
// ============================================================================

export const loadWhiteboards = async (
  profileId: string,
): Promise<Whiteboard[]> => {
  const firestore = getFirestore();
  const whiteboardsRef = collection(firestore, "whiteboards");
  const q = query(whiteboardsRef, where("profileId", "==", profileId));
  const snapshot = await getDocs(q);

  // Load metadata only (without S3 downloads) for fast initial load
  const whiteboards = snapshot.docs.map((docSnapshot) => {
    const data = docSnapshot.data();

    // Return metadata only, with empty elements/files
    // Actual content will be loaded on-demand via loadWhiteboardContent()
    return {
      id: docSnapshot.id,
      profileId: data.profileId,
      name: data.name,
      createdAt: data.createdAt,
      updatedAt: data.updatedAt,
      lastOpened: data.lastOpened,
      thumbnailUrl: data.thumbnailUrl,
      folderId: data.folderId ?? null,
      pinned: data.pinned ?? false,
      elements: [],
      files: {},
    } as Whiteboard;
  });

  return whiteboards;
};

/**
 * Load the full content (elements and files) for a specific whiteboard.
 * This is called on-demand when a whiteboard is opened.
 */
export const loadWhiteboardContent = async (
  whiteboardId: string,
): Promise<{ elements: any[]; files: Record<string, any> }> => {
  const firestore = getFirestore();
  const whiteboardRef = doc(firestore, "whiteboards", whiteboardId);
  const snapshot = await getDoc(whiteboardRef);

  if (!snapshot.exists()) {
    return { elements: [], files: {} };
  }

  const data = snapshot.data();

  // If data is stored in S3, download it
  if (data.isS3) {
    const s3Data = await downloadWhiteboardData(whiteboardId);
    return {
      elements: s3Data?.elements || [],
      files: s3Data?.files || {},
    };
  }

  // Legacy: If chunked, load from subcollection
  if (data.isChunked) {
    const chunksRef = collection(whiteboardRef, "chunks");
    const chunksSnapshot = await getDocs(chunksRef);

    const chunks = chunksSnapshot.docs
      .map((d) => ({ index: d.data().index, data: d.data().data }))
      .sort((a, b) => a.index - b.index);

    const combinedData = chunks.map((chunk) => chunk.data).join("");
    const parsedData = JSON.parse(combinedData);

    return {
      elements: parsedData.elements || [],
      files: parsedData.files || {},
    };
  }

  // Legacy format (inline data)
  return {
    elements:
      typeof data.elements === "string"
        ? JSON.parse(data.elements)
        : data.elements || [],
    files:
      typeof data.files === "string"
        ? JSON.parse(data.files)
        : data.files || {},
  };
};

export const getWhiteboard = async (
  whiteboardId: string,
): Promise<Whiteboard | null> => {
  const firestore = getFirestore();
  const whiteboardRef = doc(firestore, "whiteboards", whiteboardId);
  const snapshot = await getDoc(whiteboardRef);

  if (!snapshot.exists()) {
    return null;
  }

  const data = snapshot.data();

  // If data is stored in S3, download it
  if (data.isS3) {
    const s3Data = await downloadWhiteboardData(whiteboardId);
    return {
      id: snapshot.id,
      profileId: data.profileId,
      name: data.name,
      createdAt: data.createdAt,
      updatedAt: data.updatedAt,
      lastOpened: data.lastOpened,
      thumbnailUrl: data.thumbnailUrl,
      elements: s3Data?.elements || [],
      files: s3Data?.files || {},
    } as Whiteboard;
  }

  // Legacy: Check if data is chunked
  if (data.isChunked) {
    const chunksRef = collection(whiteboardRef, "chunks");
    const chunksSnapshot = await getDocs(chunksRef);

    // Reconstruct data from chunks
    const chunks = chunksSnapshot.docs
      .map((d) => ({ index: d.data().index, data: d.data().data }))
      .sort((a, b) => a.index - b.index);

    const combinedData = chunks.map((chunk) => chunk.data).join("");
    const parsedData = JSON.parse(combinedData);

    return {
      id: snapshot.id,
      profileId: data.profileId,
      name: data.name,
      createdAt: data.createdAt,
      updatedAt: data.updatedAt,
      lastOpened: data.lastOpened,
      elements: parsedData.elements || [],
      files: parsedData.files || {},
    } as Whiteboard;
  }

  // Legacy format (non-chunked)
  return {
    id: snapshot.id,
    ...data,
    elements:
      typeof data.elements === "string"
        ? JSON.parse(data.elements)
        : data.elements || [],
    files:
      typeof data.files === "string"
        ? JSON.parse(data.files)
        : data.files || {},
  } as Whiteboard;
};

export const createWhiteboard = async (
  profileId: string,
  name: string,
): Promise<Whiteboard> => {
  const firestore = getFirestore();
  const whiteboardsRef = collection(firestore, "whiteboards");

  const now = Date.now();
  const newWhiteboard = {
    profileId,
    name,
    elements: [],
    files: {},
    createdAt: now,
    updatedAt: now,
    lastOpened: now,
  };

  const docRef = await addDoc(whiteboardsRef, newWhiteboard);

  return {
    id: docRef.id,
    ...newWhiteboard,
  };
};

// Single save state - only one save at a time, with pending data for next save
let currentSavePromise: Promise<void> | null = null;
let pendingSaveData: {
  whiteboardId: string;
  elements: any[];
  files: Record<string, any>;
} | null = null;

// Global mutex to prevent ANY concurrent saves
let saveMutex: Promise<void> = Promise.resolve();

// In-memory cache of last saved hash per whiteboard (avoids Firestore read on every save)
const lastSavedHashCache = new Map<string, string>();

const executeSave = async (
  whiteboardId: string,
  elements: any[],
  files: Record<string, any>,
): Promise<void> => {
  const firestore = getFirestore();
  const whiteboardRef = doc(firestore, "whiteboards", whiteboardId);

  // Create hash of data to detect actual changes (lightweight version check)
  const dataStr = JSON.stringify({ elements, files });
  const dataHash = await crypto.subtle.digest(
    "SHA-256",
    new TextEncoder().encode(dataStr),
  );
  const hashHex = Array.from(new Uint8Array(dataHash))
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");

  // Check in-memory cache first (avoids Firestore read on every save)
  if (lastSavedHashCache.get(whiteboardId) === hashHex) {
    return;
  }

  // Upload data to S3 (single HTTP PUT, no Firestore write limits)
  const dataUrl = await uploadWhiteboardData(whiteboardId, elements, files);

  // Only update metadata in Firestore (tiny document, no chunks)
  await updateDoc(whiteboardRef, {
    dataUrl, // S3 URL where actual data is stored
    dataHash: hashHex,
    isS3: true, // Flag to indicate data is in S3
    isChunked: false, // No longer using chunks
    updatedAt: Date.now(),
  });

  // Store hash in memory so next save skips the Firestore read
  lastSavedHashCache.set(whiteboardId, hashHex);

  console.log(`[saveWhiteboard] Save complete for ${whiteboardId}`); // eslint-disable-line no-console
};

export const saveWhiteboard = async (
  whiteboardId: string,
  elements: any[],
  files: Record<string, any>,
): Promise<boolean> => {
  // CRITICAL: Wait for any existing save operation to complete first
  // This is a hard mutex - no two saves can ever run concurrently
  const previousSave = saveMutex;
  let resolveMutex: () => void;
  saveMutex = new Promise((resolve) => {
    resolveMutex = resolve;
  });

  await previousSave;

  try {
    // If a save is already running, just update pending data and return false
    if (currentSavePromise) {
      pendingSaveData = { whiteboardId, elements, files };
      return false; // Did not save
    }

    console.log(`[saveWhiteboard] Starting save for ${whiteboardId}`); // eslint-disable-line no-console

    // Start the save with retry loop for pending saves
    const saveLoop = async (): Promise<void> => {
      // Save current data
      await executeSave(whiteboardId, elements, files);

      // Check if more data came in while saving
      while (pendingSaveData) {
        const pending = pendingSaveData;
        pendingSaveData = null; // Clear before executing
        await executeSave(
          pending.whiteboardId,
          pending.elements,
          pending.files,
        );
      }
    };

    try {
      currentSavePromise = saveLoop();
      await currentSavePromise;
      return true; // Did save
    } finally {
      currentSavePromise = null;
    }
  } finally {
    // Always release mutex
    resolveMutex!();
  }
};

export const updateWhiteboardLastOpened = async (
  whiteboardId: string,
): Promise<void> => {
  const firestore = getFirestore();
  const whiteboardRef = doc(firestore, "whiteboards", whiteboardId);

  await updateDoc(whiteboardRef, {
    lastOpened: Date.now(),
  });
};

export const deleteWhiteboard = async (whiteboardId: string): Promise<void> => {
  const firestore = getFirestore();
  const whiteboardRef = doc(firestore, "whiteboards", whiteboardId);

  const whiteboardDoc = await getDoc(whiteboardRef);
  if (whiteboardDoc.exists()) {
    const data = whiteboardDoc.data();

    // If data is in S3, delete it
    if (data?.isS3) {
      await deleteWhiteboardData(whiteboardId);
    }

    // Legacy: Check if whiteboard has chunks subcollection
    if (data?.isChunked) {
      // Delete all chunks in batches of 500
      const chunksRef = collection(whiteboardRef, "chunks");
      const chunksSnapshot = await getDocs(chunksRef);

      if (chunksSnapshot.docs.length > 0) {
        for (let i = 0; i < chunksSnapshot.docs.length; i += 500) {
          const batch = writeBatch(firestore);
          chunksSnapshot.docs
            .slice(i, i + 500)
            .forEach((d) => batch.delete(d.ref));
          await batch.commit();
        }
      }
    }
  }

  // Delete the main whiteboard document
  await deleteDoc(whiteboardRef);
};

export const renameWhiteboard = async (
  whiteboardId: string,
  name: string,
): Promise<void> => {
  const firestore = getFirestore();
  const whiteboardRef = doc(firestore, "whiteboards", whiteboardId);

  await updateDoc(whiteboardRef, {
    name,
    updatedAt: Date.now(),
  });
};

// ============================================================================
// Password Management
// ============================================================================

export const changeProfilePassword = async (
  profileId: string,
  newPasswordHash: string,
): Promise<void> => {
  const firestore = getFirestore();
  const profileRef = doc(firestore, "profiles", profileId);

  await updateDoc(profileRef, {
    password: newPasswordHash,
  });
};

export const updateWhiteboardThumbnail = async (
  whiteboardId: string,
  thumbnailUrl: string,
): Promise<void> => {
  const firestore = getFirestore();
  const whiteboardRef = doc(firestore, "whiteboards", whiteboardId);

  await updateDoc(whiteboardRef, {
    thumbnailUrl,
  });
};

export const updateWhiteboardPinned = async (
  whiteboardId: string,
  pinned: boolean,
): Promise<void> => {
  const firestore = getFirestore();
  const whiteboardRef = doc(firestore, "whiteboards", whiteboardId);

  await updateDoc(whiteboardRef, {
    pinned,
  });
};

export const updateProfilePicture = async (
  profileId: string,
  pictureUrl: string,
): Promise<void> => {
  const firestore = getFirestore();
  const profileRef = doc(firestore, "profiles", profileId);

  await updateDoc(profileRef, {
    pictureUrl,
  });
};

export const updateProfileLocalBackup = async (
  profileId: string,
  localBackupEnabled: boolean,
): Promise<void> => {
  const firestore = getFirestore();
  const profileRef = doc(firestore, "profiles", profileId);

  await updateDoc(profileRef, {
    localBackupEnabled,
  });
};

// ============================================================================
// Whiteboard Folders
// ============================================================================

export const loadWhiteboardFolders = async (
  profileId: string,
): Promise<WhiteboardFolder[]> => {
  const firestore = getFirestore();
  const foldersRef = collection(firestore, "whiteboardFolders");
  const q = query(foldersRef, where("profileId", "==", profileId));
  const snapshot = await getDocs(q);

  return snapshot.docs.map((doc) => ({
    id: doc.id,
    ...doc.data(),
  })) as WhiteboardFolder[];
};

export const createWhiteboardFolder = async (
  profileId: string,
  name: string,
  parentId: string | null = null,
): Promise<WhiteboardFolder> => {
  const firestore = getFirestore();
  const foldersRef = collection(firestore, "whiteboardFolders");

  const newFolder = {
    profileId,
    name,
    parentId,
    createdAt: Date.now(),
  };

  const docRef = await addDoc(foldersRef, newFolder);

  return {
    id: docRef.id,
    ...newFolder,
  };
};

export const renameWhiteboardFolder = async (
  folderId: string,
  newName: string,
): Promise<void> => {
  const firestore = getFirestore();
  const folderRef = doc(firestore, "whiteboardFolders", folderId);

  await updateDoc(folderRef, {
    name: newName,
  });
};

export const deleteWhiteboardFolder = async (
  folderId: string,
  deleteWhiteboards: boolean = false,
): Promise<void> => {
  const firestore = getFirestore();
  const batch = writeBatch(firestore);

  // Find all nested folders
  const foldersRef = collection(firestore, "whiteboardFolders");
  const allFoldersSnapshot = await getDocs(foldersRef);
  const foldersToDelete = new Set<string>([folderId]);

  const findNestedFolders = (parentId: string) => {
    allFoldersSnapshot.docs.forEach((doc) => {
      const data = doc.data();
      if (data.parentId === parentId) {
        foldersToDelete.add(doc.id);
        findNestedFolders(doc.id);
      }
    });
  };

  findNestedFolders(folderId);

  // Delete all folders
  foldersToDelete.forEach((id) => {
    const folderRef = doc(firestore, "whiteboardFolders", id);
    batch.delete(folderRef);
  });

  // Handle whiteboards in deleted folders
  const whiteboardsRef = collection(firestore, "whiteboards");
  const whiteboardsSnapshot = await getDocs(whiteboardsRef);

  whiteboardsSnapshot.docs.forEach((doc) => {
    const data = doc.data();
    if (data.folderId && foldersToDelete.has(data.folderId)) {
      const whiteboardRef = doc.ref;
      if (deleteWhiteboards) {
        batch.delete(whiteboardRef);
      } else {
        batch.update(whiteboardRef, { folderId: null });
      }
    }
  });

  await batch.commit();
};

export const moveWhiteboardToFolder = async (
  whiteboardId: string,
  folderId: string | null,
): Promise<void> => {
  const firestore = getFirestore();
  const whiteboardRef = doc(firestore, "whiteboards", whiteboardId);

  await updateDoc(whiteboardRef, {
    folderId,
  });
};
